/**
 * Rocket nozzle calculator — TypeScript port of rocket_nozzle.py.
 *
 * Quasi-1D, steady, isentropic, calorically perfect gas, supersonic exit
 * (converging-diverging de Laval nozzle, choked throat).
 *
 * All quantities use SI base units inside the calculator.  Callers handle
 * unit conversions (see ./units.ts).
 */

export const G0 = 9.80665; // standard gravity, m/s^2
export const R_UNIVERSAL = 8314.462618; // J/(kmol*K)

export interface RocketNozzleInputs {
  Pt: number; // chamber total pressure, Pa
  Tt: number; // chamber total temperature, K
  gamma: number; // ratio of specific heats (dimensionless)
  MW: number; // molecular weight of combustion products, g/mol
  At: number; // throat area, m^2
  AeAt: number; // exit-to-throat area ratio (dimensionless)
  Pamb: number; // ambient/back pressure, Pa
}

export interface RocketNozzleOutputs {
  R: number; // specific gas constant, J/(kg*K)
  Ae: number; // exit area, m^2
  mdot: number; // mass flow rate, kg/s
  Mt: number; // throat Mach number (=1 for choked CD nozzle)
  Me: number; // exit Mach number
  Pe: number; // exit static pressure, Pa
  Te: number; // exit static temperature, K
  Ve: number; // exit velocity, m/s
  F: number; // thrust, N
  Isp: number; // specific impulse, s
  NPR: number; // nozzle pressure ratio (Pt/Pamb)
  cstar: number; // characteristic velocity Pt*At/mdot, m/s
  CF: number; // thrust coefficient F/(Pt*At), dimensionless
}

/** Bisection root finder — local copy so we don't pull in scipy/mathjs. */
function brentq(
  f: (x: number) => number,
  aIn: number,
  bIn: number,
  tol = 1e-12,
  maxiter = 200,
): number {
  let a = aIn;
  let b = bIn;
  let fa = f(a);
  let fb = f(b);
  if (fa * fb > 0) {
    throw new Error(
      `brentq: f(${a})=${fa} and f(${b})=${fb} do not bracket a root`,
    );
  }
  for (let i = 0; i < maxiter; i += 1) {
    const c = 0.5 * (a + b);
    const fc = f(c);
    if (Math.abs(fc) < tol || b - a < tol) return c;
    if (fa * fc < 0) {
      b = c;
      fb = fc;
    } else {
      a = c;
      fa = fc;
    }
  }
  return 0.5 * (a + b);
}

/** A/A* as a function of Mach number (isentropic). */
export function areaRatioFromMach(M: number, gamma: number): number {
  const g = gamma;
  return (
    (1 / M) *
    Math.pow(
      (2 / (g + 1)) * (1 + 0.5 * (g - 1) * M * M),
      (g + 1) / (2 * (g - 1)),
    )
  );
}

/** Invert A/A* = f(M); supersonic root by default. */
export function machFromAreaRatio(
  AR: number,
  gamma: number,
  supersonic = true,
): number {
  const f = (M: number) => areaRatioFromMach(M, gamma) - AR;
  return supersonic ? brentq(f, 1 + 1e-9, 50) : brentq(f, 1e-6, 1 - 1e-9);
}

/**
 * Full quasi-1D rocket-nozzle calculation.
 *
 * Equations (textbook isentropic-flow + rocket thrust):
 *   - mdot = (Pt*At/sqrt(Tt)) * sqrt(g/R) * ((g+1)/2)^(-(g+1)/(2(g-1)))
 *   - Ae/At = (1/Me) * [(2/(g+1)) * (1 + (g-1)/2 * Me^2)]^((g+1)/(2(g-1)))
 *   - Pe/Pt = (1 + (g-1)/2 * Me^2)^(-g/(g-1))
 *   - Te/Tt = 1 / (1 + (g-1)/2 * Me^2)
 *   - Ve    = Me * sqrt(g*R*Te)
 *   - F     = mdot*Ve + (Pe - Pamb)*Ae
 *   - Isp   = F / (mdot * g0)
 *   - cstar = Pt*At / mdot      (figure of merit for the propellant)
 *   - CF    = F / (Pt*At)       (figure of merit for the nozzle)
 *   - NPR   = Pt / Pamb
 */
export function calculateRocketNozzle(
  inputs: RocketNozzleInputs,
): RocketNozzleOutputs {
  const { Pt, Tt, gamma: g, MW, At, AeAt, Pamb } = inputs;

  const R = R_UNIVERSAL / MW;
  const Ae = AeAt * At;

  // Choked-throat mass flow.
  const mdot =
    ((Pt * At) / Math.sqrt(Tt)) *
    Math.sqrt(g / R) *
    Math.pow((g + 1) / 2, -(g + 1) / (2 * (g - 1)));

  // Exit Mach (supersonic root).
  const Me = machFromAreaRatio(AeAt, g, true);

  // Exit static pressure & temperature.
  const x = 1 + 0.5 * (g - 1) * Me * Me;
  const Pe = Pt * Math.pow(x, -g / (g - 1));
  const Te = Tt / x;

  // Exit velocity.
  const Ve = Me * Math.sqrt(g * R * Te);

  // Thrust, Isp.
  const F = mdot * Ve + (Pe - Pamb) * Ae;
  const Isp = F / (mdot * G0);

  // Derived figures of merit.
  const NPR = Pamb > 0 ? Pt / Pamb : Infinity;
  const cstar = (Pt * At) / mdot;
  const CF = F / (Pt * At);

  return {
    R,
    Ae,
    mdot,
    Mt: 1,
    Me,
    Pe,
    Te,
    Ve,
    F,
    Isp,
    NPR,
    cstar,
    CF,
  };
}
